<template>
  <div>
    <v-breadcrumbs :items="breadcrumbItems"/>
    <v-card v-if="event">
      <v-alert type="warning" v-if="event.notPublished">{{$t('eventmanager.not-published-msg')}}</v-alert>
      <EventHeader :event="event"/>

      <div v-if="event.races && event.races.length > 0">
        <v-card-title class="subtitle">{{$t('events.races-and-leaderboards')}}</v-card-title>
        <RaceGrid :items="event.races" :event="event" class="px-4"></RaceGrid>
      </div>

      <div v-if="photos && photos.length > 0">
        <v-card-title class="subtitle">{{$t('events.photos')}}</v-card-title>
        <PhotoGallery :photos="photos" :eventId="event.id" />
      </div>
  
      <v-container>
        <v-row>
          <v-col cols="12" md="8" class="px-0">
            <v-card-title class="subtitle">{{$t('events.about', {event: event.name})}}</v-card-title>
            <v-card-text>
              <vue-markdown class="markdown" :html="false">{{event.description }}</vue-markdown>
              <!-- <v-btn outlined class="mt-4 mr-4" color="primary" :to="{name: 'eventWelcome', params: {id:event.id}}">How it works</v-btn> -->
              <v-btn v-if="event.url" outlined class="mt-4 mr-4" :color="event.color || 'primary'" :href="event.url" target="_blank">{{$t('events.visit-website')}}</v-btn>
            </v-card-text>
            <div v-if="organizations.length > 0">
              <v-card-title class="subtitle pb-0">{{$t('events.organizer')}}</v-card-title>
              <v-col v-for="item in organizations" :key="item.id"
                cols="12" sm="6" md="6" lg="4"
              >
                <GroupCard :item="item" route-name="organizationView"></GroupCard>
              </v-col>
            </div>
            <div v-if="series.length > 0">
              <v-card-title class="subtitle pb-0">{{$t('events.part-of-series')}}</v-card-title>
              <v-col v-for="item in series" :key="item.id"
                cols="12" sm="6" md="6" lg="4"
              >
                <GroupCard :item="item" route-name="seriesView"></GroupCard>
              </v-col>
            </div>
            <div v-if="tileScores && tileScores.length > 0" class="mt-4">
              <v-card-title class="subtitle">Tiles this month</v-card-title>
              <v-card-text>Athletes participating in this event have scored points on these <router-link :to="{name: 'map'}">tiles</router-link>. Tile leaderboards start from scratch every new calendar month.</v-card-text>
              <v-simple-table fixed-header >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left"></th>
                      <th class="text-left">Tile</th>
                      <th class="text-left">Country</th>
                      <!-- <th class="text-left">Points</th> -->
                      <th class="text-left">Distance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in tileScores" :key="idx">
                      <td><v-icon>{{$helpers.getActivityIcon(item.type)}}</v-icon></td>
                      <td><router-link :to="{name: 'tile', params: {id: item.id, type: item.type.toLowerCase() }}">{{ item.name }}</router-link></td>
                      <td><span v-if="item.country_code" :title="item.country_code" :class="`flag-icon flag-icon-${item.country_code.toLowerCase() }`"></span></td>
                      <!-- <td class=""><ActivityScoreChip :points="item.score.total_pts"/></td> -->
                      <td>{{ item.total_dist | distance(event.unit) }} </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>            
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <EventJoinCard :event="event"/>
          </v-col>
        </v-row>
      </v-container>
      <v-divider></v-divider>

      <SodispRules v-if="event.distancing"/>
      <div class="grey lighten-4 ">
        <v-card-text>
          <v-alert v-if="isConnected" type="success">{{$t('events.profile-is-connected')}}</v-alert>
          <vue-markdown v-if="event.rules_desc" class="markdown" :html="false">{{event.rules_desc }}</vue-markdown>
          <div v-else>
            <h3 class="headline">{{$t('events.rules.title')}}</h3>
            <ul class="mb-4 mt-2">
              <li>{{$t('events.rules.join_before', { from: $options.filters.moment(event.from, "L LT"), till: $options.filters.moment(event.till, "L LT") })}}</li>
              <li>{{$t('events.rules.period', { from: $options.filters.moment(event.from, "L LT"), till: $options.filters.moment(event.till, "L LT") })}}</li>
              <li v-if="event.allowed_history_days || event.allowed_history_days === 0">{{$t('events.rules.backloading', { days: event.allowed_history_days })}}</li>
              <li v-if="event.allow_manual_entry">{{$t('events.rules.manual-entry-allowed')}}</li>
              <li v-else>{{$t('events.rules.gps-only')}}</li>
            </ul>

            <p>
              {{$t('events.local-date-times-msg')}}
            </p>
          </div>
        </v-card-text>
        
      </div>
      <v-dialog v-model="showShareBib" width="600">
        <v-card>
          <v-card-title>
            {{ event.name }}
            <v-spacer/>
            <ShareButton color="primary" outlined/>
          </v-card-title>
          <img :src="bibImageUrl" style="max-width: 100%;"/>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showShareBib = false">{{$t('shared.close')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import eventService from "@/services/eventService";
import profileService from "@/services/profileService";
import { EventBus } from '@/plugins/eventbus.js';
import ShareButton from '@/components/ShareButton.vue';
import ActivityUploadForm from '@/components/ActivityUploadForm.vue';
import SodispRules from '@/components/SodispRules.vue';
import RaceGrid from '@/components/RaceGrid.vue';
import EventJoinCard from '@/components/EventJoinCard.vue';
import EventHeader from '@/components/EventHeader.vue';
import VueMarkdown from 'vue-markdown'
import ActivityScoreChip from "@/components/ActivityScoreChip";
import GroupCard from "@/components/GroupCard";
import PhotoGallery from "@/components/PhotoGallery";
import tenants from '@/data/tenants.config'
const tenant = tenants.current();

export default {
  name: "Event",
  components: {
    VueMarkdown,
    ShareButton,
    ActivityUploadForm,
    ActivityScoreChip,
    SodispRules,
    RaceGrid,
    EventJoinCard,
    EventHeader,
    GroupCard,
    PhotoGallery,
  },
  props: {
  },
  data() {
    return {
      event: null,
      profile: null,
      status: null,
      photos: null,
      tileScores: null,
      showShareBib: false,
      bibImageUrl: false,
    };
  },
  methods: {
      
    async loadEvent(id) {
      var response = await eventService.get(id);
      this.event = response.data;
      var meta = {
        title: this.event.name + ' on ' + tenant.name,
        description: this.event.description,
        image: this.event.img,
      };
      if (this.$route.query.share === 'bib') {
        this.showShareBib = true;
        this.bibImageUrl = this.$route.query.img || this.event.img;
        meta = {
          title: `My virtual bib for the ${this.event.name}`,
          description: `I'm going to start the ${this.event.name} on ${tenant.title}`,
          image: this.bibImageUrl,
        }; 
      }

      EventBus.$emit('page-header-change', meta);
      await this.getEventStatus();
      this.photos = (await eventService.getPhotos(id)).data.data;
      //this.tileScores = (await eventService.getTileScores(id)).data.data;
    },

    async getEventStatus() {
      if (this.user) {
        var response = await profileService.getEventStatus(this.event.id);
        this.status = response.data;
      }
      else {
        //this.profile = 'Not logged in';      
      }
    },
    
    async joinEvent() {
      await profileService.joinEvent(this.event.id);
      await this.getEventStatus();
    },
    
    async testProfile() {
        try {
      var response = await profileService.get();
        }
        catch {
      this.profile = 'ERROR / UNAUTHORIZED';      
        }
      console.log('PROFILE response: ', response);
      this.profile = response.data;
    },
    

  },
  computed: {
    breadcrumbItems() {
      return !this.event ? [] : [
        { text: this.event.name, disabled: true },
      ];
    },
    series() {
      return this.event == null || this.event.linked_groups == null ? [] : this.event.linked_groups.filter(x => x.type === 'SERIES');
    },
    organizations() {
      return this.event == null || this.event.linked_groups == null ? [] : this.event.linked_groups.filter(x => x.type === 'ORGANIZATION');
    },
    isConnected() {
      return this.status && this.status.connected;
    },
    ...mapGetters({
      user: "user"
    })
  },
  async beforeRouteUpdate (to, from, next) {
    // react to route changes...
    await this.loadEvent(to.params.id);
    next();
  },
  async mounted() {
    await this.loadEvent(this.$route.params.id);
    
    EventBus.$on('login-state-change', async user => {
      //console.log('LOGIN CHANGED', user, this);
      await this.getEventStatus();
    });
  }
};
</script>
<style lang="scss">

</style>

